import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadSong } from "../../actions/catalogActions";
import { selectAnySong } from "../../selectors";
import { sortedVerses, verseLabel, verseLines } from "../../lib/lyrics";
import { songMeta } from "../common/SongRow";

/**
 * A song's words over a sheet, raised by a tap on a row (with a way to close it) or shown
 * while the row is held. Fetches the verses when the row only knew the title.
 */
const SongPreview = ({ songId, title, held = false, onClose }) => {
  const dispatch = useDispatch();
  const song = useSelector((state) => selectAnySong(state, songId));
  const [missing, setMissing] = useState(false);
  const loaded = Boolean(song?.Verses);

  useEffect(() => {
    if (loaded) return;
    dispatch(loadSong(songId)).then((found) => {
      if (!found) setMissing(true);
    });
  }, [dispatch, songId, loaded]);

  useEffect(() => {
    if (held) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [held, onClose]);

  const name = song?.Title || title;

  return (
    <div className="pv-layer">
      <div className="pv-card" role="dialog" aria-label={`Preview of ${name}`} data-screen-label="Song preview">
        <div className="pv-head">
          {held ? <span className="pv-hold">Hold</span> : <span />}
          {!held && (
            <button type="button" className="btn btn-round btn-xs btn-outline" onClick={onClose} aria-label="Close the preview">
              ×
            </button>
          )}
        </div>
        <div className="pv-title">{name}</div>
        {song && <div className="pv-meta">{songMeta(song)}</div>}
        <div className="pv-body">
          {sortedVerses(song).map((verse, index) => (
            <div key={verse.Order ?? index} className="pv-sec">
              <span className="kicker">{verseLabel(verse, index)}</span>
              {verseLines(verse).map((line, position) => (
                <span key={position} className="pv-line">
                  {line.text || " "}
                </span>
              ))}
            </div>
          ))}
          {!loaded && !missing && <div className="pv-muted">Loading…</div>}
          {missing && <div className="pv-muted">That song is no longer available.</div>}
        </div>
      </div>
    </div>
  );
};

export default SongPreview;
